import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getProducts, getCategories, getReviews, getLessons, getOffers, getCoupons, seedIfEmpty } from '../lib/firestore';
import { PRODUCTS as MOCK_PRODUCTS, CATEGORIES as MOCK_CATEGORIES, REVIEWS as MOCK_REVIEWS } from '../mock/data';

const DataContext = createContext();
export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [offers, setOffers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeded, setSeeded] = useState(false);

  const refresh = useCallback(async () => {
    // Products/categories drive the homepage's and shop's above-the-fold content, so
    // they're fetched and applied to state as soon as they're ready — independently of
    // the reviews/lessons/offers/coupons queries below, which nothing needs for first
    // paint. Previously all six were awaited together, so the whole storefront sat blank
    // until the slowest of the six resolved (reviews has a bundled mock fallback with no
    // such gate, so it would flash in first while everything else stayed empty).
    const core = Promise.all([getProducts(), getCategories()])
      .then(([p, c]) => {
        // Firestore doesn't guarantee document order across reads; sort explicitly so
        // category/product ordering (and therefore which images show where) stays stable
        // between page loads instead of shuffling.
        setProducts([...p].sort((a, b) => (a.nameEn || a.id).localeCompare(b.nameEn || b.id)));
        setCategories([...c].sort((a, b) => (a.nameEn || a.id).localeCompare(b.nameEn || b.id)));
      })
      .catch((e) => {
        console.error('Firestore fetch error (products/categories):', e);
        setProducts(MOCK_PRODUCTS);
        setCategories(MOCK_CATEGORIES);
      });

    const secondary = Promise.all([getReviews(), getLessons(), getOffers(), getCoupons()])
      .then(([r, l, o, cp]) => {
        // Left empty when the admin hasn't added any reviews yet — components that want a
        // friendly default (e.g. the homepage testimonials carousel) fall back to mock data
        // themselves, so this stays the true Firestore state for the admin reviews table.
        setReviews(r);
        // Same story for lessons — the public /lessons page falls back to the mock BLOGS
        // list itself when this is empty, so this stays the true Firestore state for admin.
        setLessons(l);
        // Offers/coupons with no admin entries yet just means no promo banners/codes show
        // anywhere — no mock fallback needed since an empty list is a perfectly valid real state.
        setOffers(o);
        setCoupons(cp);
      })
      .catch((e) => {
        console.error('Firestore fetch error (reviews/lessons/offers/coupons):', e);
        setReviews(MOCK_REVIEWS);
        setLessons([]);
        setOffers([]);
        setCoupons([]);
      });

    // `loading` still only clears once everything has settled, same as before — admin
    // pages that gate a whole table render on it (AdminLessons, AdminOffers, etc.) keep
    // their existing "wait for it all" behavior unchanged.
    await Promise.all([core, secondary]);
    setLoading(false);
  }, []);

  const seedDatabase = async () => {
    setLoading(true);
    try {
      const res = await seedIfEmpty(MOCK_PRODUCTS, MOCK_CATEGORIES);
      await refresh();
      setSeeded(true);
      return res;
    } catch (e) {
      console.error('Seed error:', e);
      throw e;
    }
  };

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <DataContext.Provider value={{ products, categories, reviews, lessons, offers, coupons, loading, refresh, seedDatabase, seeded }}>
      {children}
    </DataContext.Provider>
  );
};
